import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { devPortalLogin } from '../utils/api'
import logo from '../assets/logo.png'

const STORAGE_KEY_USER = 'dp_saved_username'
const STORAGE_KEY_PASS = 'dp_saved_password'

export default function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [form, setForm] = useState({
    username: localStorage.getItem(STORAGE_KEY_USER) || import.meta.env.VITE_DEV_PORTAL_USERNAME || '',
    password: localStorage.getItem(STORAGE_KEY_PASS) || import.meta.env.VITE_DEV_PORTAL_PASSWORD || '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { data } = await devPortalLogin(form.username, form.password)
      localStorage.setItem(STORAGE_KEY_USER, form.username)
      localStorage.setItem(STORAGE_KEY_PASS, form.password)
      login(data.access_token, form.username)
      navigate('/')
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Usuário ou senha inválidos.')
      } else {
        setError('Não foi possível conectar. O backend está rodando?')
      }
    } finally {
      setLoading(false)
    }
  }

  const codeTokens = [
    { text: '{  }',  x: '8%',  y: '12%', size: '1.1rem',  delay: '0s',   dur: '18s' },
    { text: '=>',    x: '82%', y: '8%',  size: '0.85rem', delay: '3s',   dur: '22s' },
    { text: '</>',   x: '15%', y: '72%', size: '1rem',    delay: '1s',   dur: '20s' },
    { text: '[ ]',   x: '75%', y: '65%', size: '0.9rem',  delay: '5s',   dur: '25s' },
    { text: 'const', x: '88%', y: '35%', size: '0.75rem', delay: '2s',   dur: '19s' },
    { text: '/**/',  x: '5%',  y: '45%', size: '0.8rem',  delay: '7s',   dur: '23s' },
    { text: '( )',   x: '55%', y: '85%', size: '1rem',    delay: '4s',   dur: '21s' },
    { text: '=>',    x: '38%', y: '6%',  size: '0.75rem', delay: '6s',   dur: '17s' },
    { text: '_;',    x: '92%', y: '78%', size: '0.9rem',  delay: '0.5s', dur: '24s' },
    { text: '{ }',   x: '62%', y: '18%', size: '0.8rem',  delay: '8s',   dur: '20s' },
    { text: '//',    x: '28%', y: '90%', size: '0.85rem', delay: '3.5s', dur: '22s' },
    { text: '<>',    x: '48%', y: '55%', size: '0.7rem',  delay: '9s',   dur: '26s' },
  ]

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: '#0A1C28' }}>
      <style>{`
        @keyframes floatUp {
          0%   { transform: translateY(0px) rotate(0deg);   opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(-60px) rotate(6deg); opacity: 0; }
        }
        @keyframes orb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33%      { transform: translate(40px, -30px) scale(1.08); }
          66%      { transform: translate(-20px, 20px) scale(0.95); }
        }
        @keyframes orb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40%      { transform: translate(-50px, 30px) scale(1.1); }
          70%      { transform: translate(30px, -20px) scale(0.92); }
        }
        @keyframes orb3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(25px, 40px) scale(1.05); }
        }
      `}</style>

      {/* grid overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(37,198,164,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(37,198,164,0.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* gradient orbs */}
      <div className="absolute w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(37,198,164,0.2) 0%, transparent 70%)', top: '-10%', left: '-10%', animation: 'orb1 14s ease-in-out infinite' }} />
      <div className="absolute w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(10,28,40,0.8) 0%, transparent 70%)', bottom: '-5%', right: '-5%', animation: 'orb2 18s ease-in-out infinite' }} />
      <div className="absolute w-[300px] h-[300px] rounded-full blur-[80px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(37,198,164,0.1) 0%, transparent 70%)', top: '40%', right: '20%', animation: 'orb3 22s ease-in-out infinite' }} />

      {/* floating code tokens */}
      {codeTokens.map((t, i) => (
        <span
          key={i}
          className="absolute select-none pointer-events-none font-mono font-light"
          style={{
            left: t.x, top: t.y,
            fontSize: t.size,
            color: i % 2 === 0 ? '#25C6A4' : 'rgba(37,198,164,0.5)',
            opacity: 0,
            animation: `floatUp ${t.dur} ${t.delay} ease-in-out infinite`,
            textShadow: `0 0 12px currentColor`,
          }}
        >
          {t.text}
        </span>
      ))}

      <div className="relative w-full max-w-sm mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
              <img src={logo} alt="app" className="w-20 h-20 object-contain rounded-full" />
            </div>
          </div>
          <h1 className="text-3xl tracking-wide" style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>
            Solid<span style={{ color: '#25C6A4' }}>.</span>Flow
          </h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Stolzl', sans-serif", color: 'rgba(255,255,255,0.5)' }}>
            Developer Portal
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-xl shadow-2xl p-8 backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(10,28,40,0.9)',
            border: '1px solid rgba(37,198,164,0.2)',
            boxShadow: '0 0 40px rgba(37,198,164,0.08)'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Username
              </label>
              <input
                type="text"
                autoFocus
                autoComplete="off"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                className="w-full rounded-md px-3 py-2.5 text-sm outline-none transition"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(37,198,164,0.25)',
                  color: '#FFFFFF',
                  colorScheme: 'dark',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                placeholder="devportal"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Password
              </label>
              <input
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full rounded-md px-3 py-2.5 text-sm outline-none transition"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(37,198,164,0.25)',
                  color: '#FFFFFF',
                  colorScheme: 'dark',
                }}
                onFocus={e => e.target.style.borderColor = 'rgba(37,198,164,0.7)'}
                onBlur={e => e.target.style.borderColor = 'rgba(37,198,164,0.25)'}
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm rounded-md px-3 py-2" style={{ color: '#f44747', backgroundColor: 'rgba(244,71,71,0.1)', border: '1px solid rgba(244,71,71,0.3)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full font-medium py-2.5 rounded-md text-sm transition flex items-center justify-center gap-2 text-white"
              style={{
                background: loading ? 'rgba(37,198,164,0.5)' : 'linear-gradient(135deg, #25C6A4, #1aa085)',
                boxShadow: '0 4px 20px rgba(37,198,164,0.3)',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = '0 4px 28px rgba(37,198,164,0.5)' }}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,198,164,0.3)'}
            >
              {loading ? (
                <>
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                  Entrando…
                </>
              ) : 'Entrar'}
            </button>
          </form>

          <div className="mt-6 pt-5 flex items-center justify-center gap-1.5 text-xs" style={{ borderTop: '1px solid rgba(37,198,164,0.15)', color: 'rgba(255,255,255,0.25)' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Credenciais fornecidas por <span style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF', marginLeft: '4px' }}>Solid<span style={{ color: '#25C6A4' }}>.</span>Flow<span style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>®</span></span>
          </div>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.2)' }}>
          Powered by <span style={{ fontFamily: 'Blare, sans-serif', color: '#FFFFFF' }}>Solid<span style={{ color: '#25C6A4' }}>.</span>Flow</span>
        </p>
      </div>
    </div>
  )
}
